import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

import DataTable from '../data_table/DataTable';
import { optimizedCellClassName } from '../data_table/tableClasses';
import { cx } from '../ui/classNames';
import Skeleton from '../ui/Skeleton';
import { negativeCell } from '../../styles/tailwindClasses';
import { formatInteger, formatPercentPoints } from '../../utils/riskOverviewPresentation';
import { buildRiskFactorSheetRows } from '../../utils/riskOverviewWorkbook';
import RiskWorkbookExportButton from '../risk_overview/RiskWorkbookExportButton';
import CompactPanelHeader from '../commonComponents/CompactPanelHeader';
import {
    compactIconButtonPressedSx,
    compactIconButtonSx,
} from '../pos_sizing_table/tableStyles';

const formatDecimal = (value) => (
    value == null || Number.isNaN(Number(value)) ? '-' : Number(value).toFixed(2)
);

const isTotalRow = (row) => row.type === 'summary' || row.type === 'category-total';

// The presentation (utils/riskOverviewPresentation.js) orders the rows
// inside their groups and numbers the stripes (`_stripeIndex`), so the
// table neither sorts nor stripes them itself. Row looks:
// - bucket rows: one full-width cell, header background, small uppercase
//   label
// - category totals: an accent tint with a 3px accent edge, their own
//   divider, and no optimized-column tint over it
// - factor and summary rows: striped by `_stripeIndex`
// - the selected factor: the table's selected look, its label semibold
const ROW_CLASS_NAMES = Object.freeze({
    bucket: '!bg-tbl-header',
    categoryTotal: cx(
        '!bg-accent/10 hover:!bg-accent/[0.15] dark:!bg-accent/[0.22] dark:hover:!bg-accent/[0.29]',
        '[&>td]:!border-b-accent/[0.28] [&>td]:[background-image:none]',
        '[&>td:first-child]:shadow-[inset_3px_0_rgb(var(--c-accent))]',
    ),
    odd: '!bg-tbl-stripe hover:!bg-ui-row-hover',
    selected: '[&>td:first-child]:font-semibold',
});

const renderBucketRow = (row) => (
    row.type === 'bucket' ? (
        <span className="text-[11px] font-bold uppercase leading-[1.2] tracking-normal text-content-secondary">
            {row.label}
        </span>
    ) : null
);

const numericColumn = ({ id, header, formatter, optimized = false, size }) => ({
    id,
    header,
    accessorFn: (row) => row[id],
    size,
    cell: ({ row, getValue }) => {
        if (row.original.isPlaceholder) return null;
        const value = getValue();
        return (
            <span className={cx(isTotalRow(row.original) && 'font-bold', value != null && Number(value) < 0 && negativeCell)}>
                {formatter(value)}
            </span>
        );
    },
    meta: { align: 'right', cellClassName: optimized ? optimizedCellClassName : undefined },
});

const columns = [
    {
        id: 'label',
        header: 'Factor',
        accessorFn: (row) => row.label,
        size: 135,
        cell: ({ row, getValue }) => {
            if (row.original.isPlaceholder) return null;
            return <span className={cx(isTotalRow(row.original) && 'font-bold')}>{getValue()}</span>;
        },
    },
    numericColumn({ id: 'percentVar', header: '% Var', formatter: formatPercentPoints, size: 82 }),
    numericColumn({ id: 'percentVarOpt', header: '% Var OPT', formatter: formatPercentPoints, optimized: true, size: 90 }),
    numericColumn({ id: 'exposure', header: 'Exposure', formatter: formatInteger, size: 92 }),
    numericColumn({ id: 'exposureOpt', header: 'Exp. OPT', formatter: formatInteger, optimized: true, size: 98 }),
    numericColumn({ id: 'euler', header: 'Fac. Risk', formatter: formatInteger, size: 96 }),
    numericColumn({ id: 'eulerOpt', header: 'Fac. Risk OPT', formatter: formatInteger, optimized: true, size: 108 }),
    numericColumn({ id: 'zscore', header: 'Z-Score', formatter: formatDecimal, size: 90 }),
];

// Factor rows open the factor's details, when the actual portfolio has them.
const canSelectFactorRow = (row) => (
    row.type === 'factor' && !row.isPlaceholder && row.actualDetailAvailable !== false
);

export default function RiskFactorsTable({
    presentation,
    selectedFactor = '',
    onSelectFactor,
    sorting,
    onSortingChange,
    directExportEnabled,
    showAllModelFactors = false,
    onToggleShowAllModelFactors = null,
    canShowAllModelFactors = false,
    headerActions = null,
    onOpenPortfolioImports = null,
    isLoading = false,
    optimizedLoading = false,
}) {
    const hasData = (presentation?.rows || []).some((r) => r.type === 'factor' && !r.isPlaceholder);
    const isInitialLoading = isLoading && !hasData;
    const loadingColumns = useMemo(() => columns.map((column) => {
        const loading = column.id.endsWith('Opt') ? optimizedLoading : isLoading;
        if (!loading || column.id === 'label') return column;
        return {
            ...column,
            cell: ({ row }) => (row.original.isPlaceholder
                ? null : <Skeleton width="70%" className="ml-auto" />),
        };
    }), [isLoading, optimizedLoading]);
    const factorSheetRows = useMemo(() => buildRiskFactorSheetRows(
        presentation.summaryExportRows,
        presentation.groupedExportRows,
    ), [presentation.groupedExportRows, presentation.summaryExportRows]);
    const exportSheets = useMemo(() => ([{
        name: 'risk_factor',
        kind: 'aoa',
        rows: factorSheetRows,
    }]), [factorSheetRows]);
    const selectedRowId = presentation.rows.find(
        (row) => selectedFactor && canSelectFactorRow(row) && row.factor === selectedFactor,
    )?.id ?? null;
    const getRowClassName = (row) => {
        if (row.type === 'bucket') return ROW_CLASS_NAMES.bucket;
        if (row.type === 'category-total') return ROW_CLASS_NAMES.categoryTotal;
        if (row.id === selectedRowId) return ROW_CLASS_NAMES.selected;
        return (row._stripeIndex || 0) % 2 === 1 ? ROW_CLASS_NAMES.odd : undefined;
    };

    return (
        <Box
            aria-busy={isLoading || optimizedLoading || undefined}
            aria-label={isInitialLoading ? 'Loading risk factors' : 'Risk factors table'}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            <CompactPanelHeader title="Risk Factors">
                {headerActions}
                {onToggleShowAllModelFactors && (
                    <Tooltip title={showAllModelFactors ? 'Show exposed factors' : 'Show all model factors'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={onToggleShowAllModelFactors}
                                disabled={!canShowAllModelFactors}
                                aria-pressed={showAllModelFactors}
                                aria-label={showAllModelFactors
                                    ? 'Show exposed factors'
                                    : 'Show all model factors'}
                                sx={[
                                    compactIconButtonSx,
                                    showAllModelFactors ? compactIconButtonPressedSx : null,
                                ]}
                            >
                                {showAllModelFactors
                                    ? <UnfoldLessRoundedIcon />
                                    : <UnfoldMoreRoundedIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {onOpenPortfolioImports && (
                    <Tooltip title="Open portfolio imports">
                        <IconButton
                            size="small"
                            onClick={onOpenPortfolioImports}
                            aria-label="Open portfolio imports"
                            sx={compactIconButtonSx}
                        >
                            <CloudUploadOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <RiskWorkbookExportButton
                    tooltip="Download risk factors"
                    ariaLabel="Download risk factors"
                    fileName="risk_factor.xlsx"
                    sheets={exportSheets}
                    disabled={
                        !directExportEnabled
                        || isInitialLoading
                        || presentation.summaryExportRows.length === 0
                    }
                    sx={compactIconButtonSx}
                />
            </CompactPanelHeader>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataTable
                    aria-label="Risk factors"
                    data={presentation.rows}
                    columns={loadingColumns}
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    manualSorting
                    striped={false}
                    getRowClassName={getRowClassName}
                    renderFullWidthRow={renderBucketRow}
                    selectedRowId={selectedRowId}
                    onRowClick={onSelectFactor ? (row) => onSelectFactor(row.factor) : undefined}
                    isRowClickable={canSelectFactorRow}
                    className="h-full"
                    rowHeight={32}
                    loading={isInitialLoading}
                    loadingRowCount={12}
                />
            </Box>
        </Box>
    );
}

RiskFactorsTable.propTypes = {
    presentation: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        summaryExportRows: PropTypes.array.isRequired,
        groupedExportRows: PropTypes.object.isRequired,
    }).isRequired,
    selectedFactor: PropTypes.string,
    onSelectFactor: PropTypes.func,
    sorting: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        desc: PropTypes.bool.isRequired,
    })).isRequired,
    onSortingChange: PropTypes.func.isRequired,
    directExportEnabled: PropTypes.bool.isRequired,
    showAllModelFactors: PropTypes.bool,
    onToggleShowAllModelFactors: PropTypes.func,
    canShowAllModelFactors: PropTypes.bool,
    headerActions: PropTypes.node,
    onOpenPortfolioImports: PropTypes.func,
    isLoading: PropTypes.bool,
    optimizedLoading: PropTypes.bool,
};
